import {NgModule} from '@angular/core';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatChip} from '@angular/material/chips';
import {MatDivider} from '@angular/material/divider';
import {
    MatAccordion,
    MatExpansionModule,
    MatExpansionPanel,
    MatExpansionPanelDescription,
    MatExpansionPanelTitle
} from '@angular/material/expansion';
import {MatGridList, MatGridTile} from '@angular/material/grid-list';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from '@angular/material/tabs';
import {MatTooltip} from '@angular/material/tooltip';
import {RouterModule} from '@angular/router';
import {icons, LucideAngularModule} from 'lucide-angular';
import {AllAssignmentsComponent} from './assignments/all-assignments/all-assignments.component';
import {DetailAssignmentsComponent} from './assignments/detail-assignments/detail-assignments.component';
import {CalendarComponent} from './calendar/calendar.component';
import {DocumentsSearchComponent} from './documents-search/documents-search.component';
import {CodeInputComponent} from './form/inputs/code-input/code-input.component';
import {HeaderComponent} from './header/header.component';
import {DefaultSelectComponent} from './form/selects/default-select/default-select.component';
import {FormComponent} from './form/form.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ButtonMoreComponent} from './buttons/button-more/button-more.component';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {PaginationComponent} from './pagination/pagination.component';
import {ModalComponent} from './modal/modal.component';
import {FileQueueComponent} from './file-queue/file-queue.component';
import {FolderAndFileTableComponent} from './folder-and-file-table/folder-and-file-table.component';
import {AlertComponent} from './alert/alert.component';
import {InputFileComponent} from './form/inputs/input-file/input-file.component';
import {TranslateModule} from '@ngx-translate/core';
import {NgxMaskModule} from 'ngx-mask';
import {
    DepartmentsMultiselectComponent
} from './form/selects/departments-multiselect/departments-multiselect.component';
import {UsersMultiselectComponent} from './form/selects/users-multiselect/users-multiselect.component';
import {MobilePopupComponent} from './mobile-popup/mobile-popup.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {CalendarModule} from 'primeng/calendar';
import {DateTimePickerComponent} from './form/inputs/date-time-picker/date-time-picker.component';
import {SearchCollectionComponent} from './form/selects/search-collection/search-collection.component';
import {
    CoupleUsersMultiselectComponent,
} from './form/selects/couple-users-multiselect/couple-users-multiselect.component';
import {SearchQueryComponent} from './form/selects/search-query/search-query.component';
import {FileNavigatorComponent} from './file-navigator/file-navigator.component';
import {LoadingComponent} from './loading/loading.component';
import {ButtonCreateFcfvComponent} from './buttons/button-create-fcfv/button-create-fcfv.component';
import {ButtonCreateTaskComponent} from './buttons/button-create-task/button-create-task.component';
import {ButtonHeaderBackComponent} from './buttons/button-header-back/button-header-back.component';
import {TasksTableComponent} from './tasks-table/tasks-table.component';
import {TaskDetailComponent} from './task-detail/task-detail.component';
import {CkeditorComponent} from './form/inputs/ckeditor/ckeditor.component';
import {CKEditorModule} from '@ckeditor/ckeditor5-angular';
import {InputInlineComponent} from './form/inputs/input-inline/input-inline.component';
import {ReplenishmentComponent} from './payments/replenishment/replenishment.component';
import {PayoutComponent} from './payments/payout/payout.component';
import {BarterComponent} from './payments/barter/barter.component';
import {ButtonCreateContractComponent} from './buttons/button-create-contract/button-create-contract.component';
import {PaymentModalComponent} from './payments/payment-modal/payment-modal.component';
import {ProfileCardModalComponent} from './modal/profile-card-modal/profile-card-modal.component';
import {TaskColumnComponent} from './task-column/task-column.component';
import {ColorPickerComponent} from './form/selects/color-picker/color-picker.component';
import {AppIconPickerComponent} from './form/selects/icon-picker/icon-picker.component';
import {HeaderPageComponent} from './header-page/header-page.component';
import {NgxColorsModule} from 'ngx-colors';
import {BoardSettingsModalComponent} from './modal/board-settings-modal/board-settings-modal.component';
import {TaskDashboardsComponent} from './task-dashboards/task-dashboards.component';
import {AvatarComponent} from '../controllers/project-control/avatar/avatar.component';
import {ButtonModalCloseComponent} from './buttons/button-modal-close/button-modal-close.component';
import {StatusButtonsComponent} from './buttons/status-buttons/status-buttons.component';
import {ChartModule} from 'primeng/chart';
import {ButtonsBankAlertComponent} from './buttons/buttons-bank-alert/buttons-bank-alert.component';
import {DocViewComponent} from './doc-view/doc-view.component';
import {FileToolbarComponent} from './doc-view/file-toolbar/file-toolbar.component';
import {SignersComponent} from './doc-view/signers/signers.component';
import {TaskDetailCommentsComponent} from './task-detail/task-detail-comments/task-detail-comments.component';
import {TaskDetailLogsComponent} from './task-detail/task-detail-logs/task-detail-logs.component';
import {PdfJsViewerModule} from 'ng2-pdfjs-viewer';
import {PropertiesComponent} from './property-settings/properties/properties.component';
import {SelectComponent} from './form/selects/select/select.component';
import {CatalogsComponent} from './property-settings/catalogs/catalogs.component';
import {DetailPropertyComponent} from './property-settings/detail-property/detail-property.component';
import {PropertyOptionTableComponent} from './property-settings/property-option-table/property-option-table.component';
import {AccordionComponent} from './accordion/accordion.component';
import {FileOptionsComponent} from './file-options/file-options.component';
import {VisorsComponent} from './doc-view/visors/visors.component';
import {SidebarSettingsComponent} from './sidebar-settings/sidebar-settings.component';
import {BoardsAccordionComponent} from './boards-accordion/boards-accordion.component';
import {DocumentsRepoStructureComponent} from './documents-repo-structure/documents-repo-structure.component';
import {GeneralProjectSettingsComponent} from './general-project-settings/general-project-settings.component';
import {AppButtonGroupSaveCancelComponent} from './buttons/button-group-save-cancel/button-group-save-cancel.component';
import {MatButtonModule} from '@angular/material/button';
import {ToastNotifyComponent} from "./toast-notify/toast-notify.component";
import {MatDialogModule} from '@angular/material/dialog';
import {AppContractAvatarComponent} from './contract-avatar/contract-avatar.component';
import {AppDocVersionListComponent} from './doc-view/doc-version-list/doc-version-list.component';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {AvatarPanelComponent} from './avatar-panel/avatar-panel.component';
import {AvatarListComponent} from './avatar-list/avatar-list.component';
import {AvatarSelectListComponent} from './avatar-list-select/avatar-list-select.component';

@NgModule({
    declarations: [
        DefaultSelectComponent, //deprecated? (use in FormComponent)
        SearchCollectionComponent, //deprecated? (use in FormComponent)
        CoupleUsersMultiselectComponent, //deprecated? (use in FormComponent)
        SearchQueryComponent, //deprecated? (use in FormComponent)
        InputFileComponent, //deprecated? (use in FormComponent)
        DepartmentsMultiselectComponent, //deprecated? (use in FormComponent)
        UsersMultiselectComponent, //deprecated? (use in FormComponent)
        FormComponent, //deprecated? (use in DocumentsSearchComponent)
        AppContractAvatarComponent,
        AppDocVersionListComponent,
        DocumentsSearchComponent,
        HeaderComponent,
        ButtonMoreComponent,
        PaginationComponent,
        ModalComponent,
        FileQueueComponent,
        FolderAndFileTableComponent,
        AlertComponent,
        MobilePopupComponent,
        DateTimePickerComponent,
        FileNavigatorComponent,
        LoadingComponent,
        ButtonCreateFcfvComponent,
        ButtonCreateTaskComponent,
        ButtonHeaderBackComponent,
        TasksTableComponent,
        TaskDetailComponent,
        CkeditorComponent,
        CkeditorComponent,
        InputInlineComponent,
        ReplenishmentComponent,
        PayoutComponent,
        BarterComponent,
        ButtonCreateContractComponent,
        PaymentModalComponent,
        ProfileCardModalComponent,
        TaskColumnComponent,
        ColorPickerComponent,
        HeaderPageComponent,
        BoardSettingsModalComponent,
        TaskDashboardsComponent,
        AvatarComponent,
        ButtonModalCloseComponent,
        StatusButtonsComponent,
        ButtonsBankAlertComponent,
        DocViewComponent,
        FileToolbarComponent,
        SignersComponent,
        TaskDetailCommentsComponent,
        TaskDetailLogsComponent,
        ToastNotifyComponent,
        SelectComponent,
        PropertiesComponent,
        CatalogsComponent,
        DetailPropertyComponent,
        PropertyOptionTableComponent,
        AccordionComponent,
        FileOptionsComponent,
        VisorsComponent,
        SidebarSettingsComponent,
        BoardsAccordionComponent,
        DocumentsRepoStructureComponent,
        GeneralProjectSettingsComponent,
        AllAssignmentsComponent,
        DetailAssignmentsComponent,
        AppButtonGroupSaveCancelComponent,
        CalendarComponent,
        AppIconPickerComponent,
        AvatarPanelComponent,
        AvatarListComponent,
        AvatarSelectListComponent
    ],
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        MatInputModule,
        MatDialogModule,
        MatSelectModule,
        FormsModule,
        DragDropModule,
        TranslateModule,
        NgxMaskModule,
        NgbModule,
        CalendarModule,
        CKEditorModule,
        NgxColorsModule,
        ChartModule,
        PdfJsViewerModule,
        NgOptimizedImage,
        MatButtonModule,
        LucideAngularModule.pick(icons),
        LucideAngularModule,
        MatGridList,
        MatGridTile,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatChip,
        MatDivider,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelTitle,
        MatExpansionPanelDescription,
        MatIcon,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelTitle,
        MatExpansionPanelDescription,
        MatExpansionModule,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatTabGroup,
        MatTab,
        MatTabContent,
        MatTabLabel,
        MatCheckbox,
        MatTooltip,
        CodeInputComponent
    ],
    exports: [
        AppContractAvatarComponent,
        DocumentsSearchComponent,
        HeaderComponent,
        ButtonMoreComponent,
        PaginationComponent,
        ModalComponent,
        FileQueueComponent,
        FolderAndFileTableComponent,
        AlertComponent,
        InputFileComponent,
        MobilePopupComponent,
        DateTimePickerComponent,
        SearchCollectionComponent,
        UsersMultiselectComponent,
        DefaultSelectComponent,
        FileNavigatorComponent,
        LoadingComponent,
        ButtonCreateFcfvComponent,
        ButtonCreateTaskComponent,
        ButtonHeaderBackComponent,
        TasksTableComponent,
        TaskDetailComponent,
        CkeditorComponent,
        InputInlineComponent,
        ReplenishmentComponent,
        PayoutComponent,
        BarterComponent,
        ButtonCreateContractComponent,
        PaymentModalComponent,
        SearchQueryComponent,
        ProfileCardModalComponent,
        TaskColumnComponent,
        ColorPickerComponent,
        HeaderPageComponent,
        BoardSettingsModalComponent,
        TaskDashboardsComponent,
        AvatarComponent,
        ButtonModalCloseComponent,
        StatusButtonsComponent,
        ButtonsBankAlertComponent,
        ToastNotifyComponent,
        DocViewComponent,
        FileToolbarComponent,
        SignersComponent,
        TaskDetailLogsComponent,
        PropertiesComponent,
        PropertyOptionTableComponent,
        DetailPropertyComponent,
        AccordionComponent,
        SelectComponent,
        VisorsComponent,
        BoardsAccordionComponent,
        DocumentsRepoStructureComponent,
        AllAssignmentsComponent,
        DetailAssignmentsComponent,
        TaskDetailCommentsComponent,
        FileOptionsComponent, //TODO удалить вместе со страницей компонентов
        SidebarSettingsComponent, //TODO удалить вместе со страницей компонентов
        AppButtonGroupSaveCancelComponent,
        CalendarComponent,
        AppIconPickerComponent,
        AvatarPanelComponent,
        AvatarListComponent,
        AvatarSelectListComponent
    ],
})
export class ComponentsModule {
}
